import { Crosshair } from "lucide-react";
import { toast } from "sonner";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Thread, Composer, StatusPill } from "@/components/feedback/Thread";
import { approvalText, reviewTotals } from "@/lib/feedback";
import { api, errMsg } from "@/lib/api";

const ItemReview = ({ it, s, entries, projectId, reload, onPick }) => {
  const reply = (text) => api.post(`/projects/${projectId}/feedback`, { item_id: it.id, text }).then(() => { reload(); toast.success(`Reply sent on ${it.tag}`); }).catch((e) => { toast.error(errMsg(e)); return false; });
  const resolve = (e) => api.post(`/projects/${projectId}/feedback/${e.fb_id}/resolve`, { resolved: !e.resolved }).then(reload).catch((err) => toast.error(errMsg(err)));
  return (
    <div className="rounded-lg border border-slate-800 bg-[#0B0F19] p-3 space-y-3" data-testid={`review-item-${it.tag}`}>
      <div className="flex items-center gap-2">
        <span className="font-mono font-semibold text-sm">{it.tag}</span>
        <StatusPill s={s} testid={`review-status-${it.tag}`} />
        {s?.open > 0 && <span className="text-[10px] font-mono text-amber-300">{s.open} open</span>}
        <button onClick={() => onPick(it.id)} data-testid={`review-show-${it.tag}`} className="ml-auto text-[11px] text-slate-400 hover:text-cyan-300 flex items-center gap-1 transition-colors"><Crosshair size={12} />Show</button>
      </div>
      {s?.status && <p className="text-[11px] text-slate-400 -mt-2">{approvalText(s)}</p>}
      <Thread entries={entries} onResolve={resolve} />
      <Composer testid={`review-reply-${it.tag}`} placeholder="Reply to the client…" cta="Reply" onSend={reply} />
    </div>
  );
};

export const ReviewSheet = ({ open, onOpenChange, projectId, items, fb, reload, onPick }) => {
  const { approved, open: openCount } = reviewTotals(items, fb.summary);
  const withFb = items.filter((i) => fb.summary[i.id]?.count).sort((a, b) => (fb.summary[b.id].open || 0) - (fb.summary[a.id].open || 0));
  const quiet = items.length - withFb.length;
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-md bg-[#111827] border-slate-700 text-slate-100 overflow-y-auto" data-testid="review-sheet">
        <SheetHeader>
          <SheetTitle className="font-heading text-slate-100">Client review</SheetTitle>
          <SheetDescription className="text-slate-400" data-testid="review-totals">{approved} of {items.length} items approved · {openCount} open comment{openCount === 1 ? "" : "s"}</SheetDescription>
        </SheetHeader>
        <div className="mt-4 h-1.5 rounded-full bg-slate-800 overflow-hidden"><div className="h-full bg-emerald-400 transition-all" style={{ width: `${items.length ? (approved / items.length) * 100 : 0}%` }} /></div>
        <div className="mt-4 space-y-3">
          {!withFb.length && <p className="text-sm text-slate-400" data-testid="review-empty">No client feedback yet. Share a 3D link from “Share 3D” so your client can approve or comment.</p>}
          {withFb.map((it) => <ItemReview key={it.id} it={it} s={fb.summary[it.id]} entries={fb.entries.filter((e) => e.item_id === it.id)} projectId={projectId} reload={reload} onPick={(id) => { onPick(id); onOpenChange(false); }} />)}
          {withFb.length > 0 && quiet > 0 && <p className="text-xs text-slate-500">{quiet} item{quiet === 1 ? "" : "s"} without feedback yet.</p>}
        </div>
        <p className="mt-6 text-[11px] text-slate-500">A summary of new client feedback is emailed to the owner every morning at 9:00 IST.</p>
      </SheetContent>
    </Sheet>
  );
};
